import React, { useState } from 'react';

/**
 * Small modal that collects a single name (used for creating/renaming collections
 * and saving favorites as a new collection). Mirrors the iOS name-entry alert.
 * onSubmit may throw — the error is surfaced inline and the modal stays open.
 */
interface NamePromptModalProps {
  title: string;
  confirmLabel: string;
  initialValue?: string;
  placeholder?: string;
  onCancel: () => void;
  onSubmit: (name: string) => Promise<void>;
  /** Stacking level; raise it when opened from another modal (default 1000, like the other modals). */
  zIndex?: number;
}

const BORDER = 'var(--border-light)';
const TEXT = 'var(--text-accent)';

const NamePromptModal: React.FC<NamePromptModalProps> = ({
  title,
  confirmLabel,
  initialValue = '',
  placeholder = 'Collection name',
  onCancel,
  onSubmit,
  zIndex = 1000,
}) => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed) { setErr('Name is required.'); return; }
    if (trimmed.length > 255) { setErr('Name must be 255 characters or fewer.'); return; }
    try {
      setSubmitting(true);
      setErr(null);
      await onSubmit(trimmed);
    } catch (e) {
      console.error('Name prompt submit failed:', e);
      setErr('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex, padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '420px' }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>{title}</h3>
        <input
          autoFocus
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          style={{
            width: '100%', padding: '10px 12px', fontSize: '0.9375rem',
            border: `1px solid ${BORDER}`, borderRadius: '8px', boxSizing: 'border-box',
          }}
        />
        {err && <div style={{ color: 'var(--error-text)', fontSize: '0.8125rem', marginTop: '8px' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NamePromptModal;
